import React, { FC } from "react";

import {
  KpkApiMediaDownloadLevel,
  KpkApiPublicShareExportDefinition,
  KpkApiPublicShareStatus,
} from "@keepeek/api-client";
import {
  KpkMedia,
  updateUnitaryPublicSharedState,
  useShareManagerActivation,
  useShareManagerExportDefinition,
  useShareManagerSelections,
} from "@keepeek/commons";
import {
  BaseComponentProps,
  DownloadManagerConditions,
  KButton,
} from "@keepeek/refront-components";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { ShareManager as ShareManagerConfig } from "../../../models/configuration/components/shareManager";
import { useConfiguration } from "../../../providers/config/hooks";
import { useDownloadConditions } from "../../../providers/downloadManager/hooks/conditions";

const PREFIX = "UnitaryPublicShareActivate";

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  button: `${PREFIX}-button`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: "absolute",
    background: "rgba(255,255,255,1)",
    top: 0,
    left: theme.spacing(-1),
    bottom: theme.spacing(-1),
    right: theme.spacing(-1),
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  [`& .${classes.content}`]: {
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
  },

  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(1),
    fontWeight: "bold",
  },

  [`& .${classes.button}`]: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    minWidth: "50%",
  },
}));

type UnitaryPublicShareActivateProps = {
  mediaId: KpkMedia["id"];
  downloadLevel: KpkApiMediaDownloadLevel;
} & BaseComponentProps;

const UnitaryPublicShareActivate: FC<React.PropsWithChildren<UnitaryPublicShareActivateProps>> =
  function ({ mediaId, downloadLevel, id, testId = "UnitaryPublicShare", sx, className }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { handleUpdateSharePublic } = useShareManagerActivation(() =>
      enqueueSnackbar(t("share-manager.update.public-share.error.label"), {
        variant: "error",
      }),
    );
    const { selectionId } = useShareManagerSelections();
    const { exportDefinitions, selectedExportDefinition, setSelectedExportDefinition } =
      useShareManagerExportDefinition();

    // We use the download manager conditions hook because the conditions are exactly the same in the share manager
    const conditions = useDownloadConditions(selectionId);
    const setUpdateUnitaryPublicShared = useSetRecoilState(updateUnitaryPublicSharedState);
    const shareManagerConfig = useConfiguration<ShareManagerConfig>(
      CONFIGURATION_SECTION_PATH.COMPONENTS_SHARE_MANAGER,
    );
    const disableExportDefinition =
      shareManagerConfig?.unitaryPublicShare &&
      shareManagerConfig?.unitaryPublicShare?.disableExportDefinition;
    const title = !disableExportDefinition
      ? "share-manager.unitary-public-share.activation.label"
      : "share-manager.unitary-public-share.activation-no-download.label";

    function getSharePublicExportDefinition() {
      if (disableExportDefinition) {
        // Enable download by default if download section is hidden
        switch (downloadLevel) {
          case KpkApiMediaDownloadLevel.Bd:
            return KpkApiPublicShareExportDefinition.Bd;
          case KpkApiMediaDownloadLevel.HD:
            return KpkApiPublicShareExportDefinition.HD;
          default:
            return KpkApiPublicShareExportDefinition.None;
        }
      }
      return selectedExportDefinition;
    }

    return (
      <StyledBox
        id={id}
        data-testid={testId}
        className={clsx("ShareManager", className, classes.root)}
        sx={sx}
      >
        <Box className={classes.content}>
          <Typography variant="subtitle1" component="div" className={classes.title}>
            {t(title)}
          </Typography>
          {!disableExportDefinition && (
            <>
              <Select
                sx={{ margin: (theme) => `0 ${theme.spacing(4)}` }}
                value={selectedExportDefinition}
                onChange={(e) =>
                  setSelectedExportDefinition(e.target.value as KpkApiPublicShareExportDefinition)
                }
              >
                {(downloadLevel === KpkApiMediaDownloadLevel.None ||
                  downloadLevel === KpkApiMediaDownloadLevel.Bd ||
                  downloadLevel === KpkApiMediaDownloadLevel.HD) && (
                  <MenuItem key={exportDefinitions[2].id} value={exportDefinitions[2].id}>
                    {exportDefinitions[2].title}
                  </MenuItem>
                )}
                {(downloadLevel === KpkApiMediaDownloadLevel.Bd ||
                  downloadLevel === KpkApiMediaDownloadLevel.HD) && (
                  <MenuItem key={exportDefinitions[0].id} value={exportDefinitions[0].id}>
                    {exportDefinitions[0].title}
                  </MenuItem>
                )}
                {downloadLevel === KpkApiMediaDownloadLevel.HD && (
                  <MenuItem key={exportDefinitions[1].id} value={exportDefinitions[1].id}>
                    {exportDefinitions[1].title}
                  </MenuItem>
                )}
              </Select>
              <DownloadManagerConditions {...conditions} sx={{ width: "100%" }} />
            </>
          )}
          <KButton
            startIcon={<KIcon className={"fa-solid fa-circle-check"} />}
            variant="contained"
            size="large"
            className={classes.button}
            onClick={async () => {
              if (
                disableExportDefinition ||
                conditions.state ||
                (!conditions.state && !conditions.cgu?.enable && !conditions.csu?.enable)
              ) {
                await handleUpdateSharePublic(
                  mediaId,
                  KpkApiPublicShareStatus.Activated,
                  getSharePublicExportDefinition(),
                  undefined,
                  false,
                );
                setUpdateUnitaryPublicShared((prev) => prev + 1);
              }
            }}
            disabled={
              !disableExportDefinition &&
              (conditions.cgu?.enable || conditions.csu?.enable) &&
              !conditions.state
            }
          >
            {t("share-manager.unitary-public-share.action.activate-share.label")}
          </KButton>
        </Box>
      </StyledBox>
    );
  };
export default UnitaryPublicShareActivate;
